import React from 'react';
import SamSpinner from './SamSpinner';
import { AccountPanel } from './AccountPanel';
import { isBillingAccessActive } from '../billing-access';

/**
 * The billing-gate overlay screens, extracted from App.js (refactor Step 1).
 * Renders at most one of four states; renders nothing for superusers, while
 * auth is still resolving, or when access is active:
 *
 *  1. Checking:  first billing check in flight → blocking spinner overlay.
 *  2. Error:     the check itself failed → retry / sign-out overlay.
 *  3. Inactive, org owner:  locked Account panel opened on Billing.
 *  4. Inactive, non-owner:  "Payment is required" overlay with sign-out.
 *
 * Markup, styles, and gating conditions are unchanged from App.js.
 */
export default function BillingGateOverlays({ authLoading, user, billingGate, refreshBillingGate, logout }) {
  const gated = !authLoading && user?.org_id && user?.role !== 'superuser';
  if (!gated) return null;

  if (billingGate.loading) {
    return (
      <div style={billingGateStyles.overlay} role="status" aria-live="polite">
        <div style={billingGateStyles.card}>
          <SamSpinner />
          <h2 style={billingGateStyles.title}>Verifying account access</h2>
          <p style={billingGateStyles.text}>Checking the account's trial or subscription status…</p>
        </div>
      </div>
    );
  }

  if (billingGate.error) {
    return (
      <div style={billingGateStyles.overlay} role="alert">
        <div style={billingGateStyles.card}>
          <h2 style={billingGateStyles.title}>Account status unavailable</h2>
          <p style={billingGateStyles.text}>{billingGate.error}</p>
          <div style={billingGateStyles.actions}>
            <button style={billingGateStyles.primaryButton} onClick={refreshBillingGate}>Try again</button>
            <button style={billingGateStyles.secondaryButton} onClick={logout}>Sign out</button>
          </div>
        </div>
      </div>
    );
  }

  if (!isBillingAccessActive(billingGate.access)) {
    return (user?.org_role === 'owner') ? (
      <AccountPanel
        orgId={user.org_id}
        initialSection="billing"
        locked
        onClose={() => {}}
        onSignOut={logout}
      />
    ) : (
      <div style={billingGateStyles.overlay} role="alert">
        <div style={billingGateStyles.card}>
          <h2 style={billingGateStyles.title}>Payment is required</h2>
          <p style={billingGateStyles.text}>
            This account's trial or paid access is no longer active. Ask the account owner to update billing before continuing.
          </p>
          <button style={billingGateStyles.secondaryButton} onClick={logout}>Sign out</button>
        </div>
      </div>
    );
  }

  return null;
}

const billingGateStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 20000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(2, 6, 23, 0.96)',
    padding: 24,
  },
  card: {
    width: 'min(520px, 100%)',
    padding: 32,
    borderRadius: 16,
    border: '1px solid rgba(148, 163, 184, 0.22)',
    background: '#0F172A',
    color: '#E2E8F0',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.45)',
    textAlign: 'center',
  },
  title: { margin: '16px 0 8px', fontSize: 22 },
  text: { margin: '0 0 22px', color: '#94A3B8', lineHeight: 1.55 },
  actions: { display: 'flex', justifyContent: 'center', gap: 12 },
  primaryButton: {
    border: 0, borderRadius: 8, padding: '10px 16px', background: '#2563EB', color: '#fff', cursor: 'pointer',
  },
  secondaryButton: {
    border: '1px solid rgba(148,163,184,0.35)', borderRadius: 8, padding: '10px 16px', background: 'transparent', color: '#CBD5E1', cursor: 'pointer',
  },
};
